import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, ArrowRight, SignOut, SpinnerGap } from "phosphor-react";
import { API_URL } from "../auth";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  
  // Guard against double-invocation (React StrictMode / rerenders)
  const hasAttemptedRef = useRef(false);

  // If token in URL, auto-verify (once only)
  useEffect(() => {
    if (token && !hasAttemptedRef.current) {
      hasAttemptedRef.current = true;
      verifyEmail(token);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    setVerifying(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/auth/verify-email?token=${verificationToken}`, {
        credentials: "include",
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        // Response wasn't JSON
        console.error("Response parse error:", jsonErr);
        data = { detail: "Unexpected server response" };
      }
      
      if (response.ok) {
        // Success - email verified or already verified
        setVerified(true);
        if (data.status === "already_verified") {
          toast.success("Your email is already verified!");
        } else {
          toast.success("Email verified successfully!");
        }
      } else {
        // API returned an error response (400, 401, etc.)
        const errorDetail = data.detail || "Verification failed";
        
        // Treat "already verified" mentions in error as success
        if (errorDetail.toLowerCase().includes("already verified") || 
            errorDetail.toLowerCase().includes("already been verified")) {
          setVerified(true);
          toast.success("Your email is already verified!");
        } else {
          // Actual failure - invalid/expired token
          setError(errorDetail);
          toast.error(errorDetail);
        }
      }
    } catch (err) {
      // True network error (no response at all)
      console.error("Verification network error:", err);
      setError("Unable to verify email. Please check your connection and try again.");
      toast.error("Connection error. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    
    try {
      const authToken = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/auth/send-verification`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Authorization": authToken ? `Bearer ${authToken}` : "",
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === "already_verified") {
          toast.success("Your email is already verified!");
          navigate("/dashboard");
        } else {
          toast.success("Verification email sent! Check your inbox.");
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to send verification email");
      }
    } catch (err) {
      toast.error("Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("auth_token");
      navigate("/login");
    } catch (err) {
      localStorage.removeItem("auth_token");
      navigate("/login");
    }
  };

  const handleContinue = () => {
    navigate("/dashboard");
  };

  // If verified, show success
  if (verified) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-texture pointer-events-none" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-white border-2 border-zinc-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-600" weight="fill" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900 mb-2">
              Email Verified!
            </h1>
            <p className="text-zinc-600 mb-6">
              Your email has been successfully verified. You can now access all features.
            </p>
            
            <Button
              onClick={handleContinue}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
              data-testid="continue-btn"
            >
              Continue to Dashboard
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If verifying with token
  if (token && verifying) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-texture pointer-events-none" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-white border-2 border-zinc-200 p-8 text-center">
            <SpinnerGap size={48} className="text-[#FF6600] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-xl font-bold uppercase text-zinc-900">
              Verifying your email...
            </h1>
          </div>
        </div>
      </div>
    );
  }

  // Default: waiting for verification
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-texture pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border-2 border-zinc-200 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FF6600]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <EnvelopeSimple size={32} className="text-[#FF6600]" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900 mb-2">
              Verify Your Email
            </h1>
            <p className="text-zinc-600">
              We sent a verification link to your email address. Please check your inbox and click the link to verify your account.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Button
              onClick={handleResend}
              disabled={resending}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <SpinnerGap size={20} className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                "Resend Verification Email"
              )}
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full h-12 border-2 border-zinc-200 text-zinc-700 hover:bg-zinc-50"
              data-testid="logout-btn"
            >
              <SignOut size={20} className="mr-2" />
              Log Out
            </Button>
          </div>

          <p className="text-center text-xs text-zinc-400 mt-6">
            Didn&apos;t receive the email? Check your spam folder or click resend above.
          </p>
        </div>
      </div>
    </div>
  );
}
